import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { useEvent } from "shared/lib/useEvent"
import { useFormSubmit } from "shared/lib/useFormSubmit"
import { RoomTypename } from "shared/types/Project"

import { useUpdateAttachmentLinks } from "entities/attachments/api/useUpdateAttachmentLinks"
import { useProjectChildPosition } from "entities/projects/model/useProjectChildPosition"
import { useRoomFragment } from "entities/rooms/model/useRoomFragment"
import { useRoomMutations } from "entities/rooms/model/useRoomMutations"
import { RoomFormData } from "features/room-management/types/RoomFormData"
import { useRoomForm } from "widgets/room/room-form/model/useRoomForm"

import { EditRoomOverlayModel } from "../EditRoomOverlay"
import { EditRoomOverlayConnectorProps } from "../EditRoomOverlayConnector"

export function useEditRoomOverlayModel({
  payload: { projectID, roomID, defaultTab }
}: EditRoomOverlayConnectorProps): EditRoomOverlayModel {
  const { closeOverlay } = useCurrentOverlay()
  const { updateRoom } = useRoomMutations()
  const updateAttachmentLinks = useUpdateAttachmentLinks()
  const room = useRoomFragment(roomID)

  const submitRoom = useEvent(async (roomUpdates: RoomFormData) => {
    const results = await Promise.all([
      updateRoom({
        projectID,
        displayPosition: roomUpdates.displayPosition,
        room: { ...roomUpdates.room, id: roomID }
      }),
      roomUpdates.attachments
        ? updateAttachmentLinks({
            id: roomID,
            resourceType: RoomTypename,
            attachmentLinks: roomUpdates.attachments
          })
        : Promise.resolve({ data: {}, errors: undefined })
    ])
    const errors = results.flatMap(r => r?.errors ?? [])
    return { data: {}, errors: errors.length ? errors : undefined }
  })

  const { handleSubmit, isSubmitting } = useFormSubmit<RoomFormData>({
    onSubmit: submitRoom,
    onSuccess: closeOverlay
  })

  const { displayPosition, parent } = useProjectChildPosition({
    projectID,
    child: room ?? null
  })

  const roomFormModel = useRoomForm({
    projectID,
    parentID: parent?.id || projectID,
    displayPosition,
    onSubmit: handleSubmit,
    formOptions: { hideProject: true },
    room
  })

  return {
    roomFormModel,
    isLoading: isSubmitting,
    isSubmitting,
    defaultTab,
    onCancel: closeOverlay
  }
}
